import React, { useEffect, useMemo, useState } from 'react';
import { socketService } from '../../services/SocketService';

const CONTAINER_LABELS = {
  healing_container: 'Healing Bag',
  magic_container: 'Magic Satchel',
  battle_container: 'Battle Kit',
  arrow_container: 'Quiver',
  poison_container: 'Poison Vials',
  general: 'Backpack',
  null: 'Backpack',
  undefined: 'Backpack',
};

const CONTAINER_CAPACITY = {
  healing_container: 9,
  magic_container: 9,
  battle_container: 9,
  arrow_container: 9,
  poison_container: 9,
  general: Infinity,
};

const TYPE_EMOJI = {
  healing: '❤️',
  mana: '💙',
  poison: '☠️',
  strength: '💪',
  defense: '🛡️',
  speed: '⚡',
  ammo: '🏹',
  arrow: '🏹',
};

const DEFAULT_THEME_ID = process.env.REACT_APP_DEFAULT_THEME_ID || 'soulwars-default';

function formatContainerLabel(key) {
  if (!key) return CONTAINER_LABELS.null;
  return CONTAINER_LABELS[key] || key.replace(/_/g, ' ');
}

function formatItemIcon(themeId, iconKey) {
  if (!iconKey) return null;
  const awsRegion = process.env.REACT_APP_AWS_REGION || 'us-east-1';
  const resolvedTheme = themeId || DEFAULT_THEME_ID;
  if (!resolvedTheme) return null;
  return `https://soulwars.s3.${awsRegion}.amazonaws.com/themes/${resolvedTheme}/items/${iconKey}.png`;
}

function formatItemEffect(effect) {
  if (!effect) return '';
  const parts = [];
  if (effect.heal) parts.push(`Heals ${effect.heal} HP`);
  if (effect.mana) parts.push(`Restores ${effect.mana} MP`);
  if (effect.dot) parts.push(`DOT ${effect.dot}/tick${effect.ticks ? ` × ${effect.ticks}` : ''}`);
  if (effect.attackBoost) parts.push(`+${Math.round(effect.attackBoost * 100)}% attack${effect.durationSec ? ` for ${effect.durationSec}s` : ''}`);
  if (effect.defenseBoost) parts.push(`+${Math.round(effect.defenseBoost * 100)}% defense${effect.durationSec ? ` for ${effect.durationSec}s` : ''}`);
  if (effect.speedBoost) parts.push(`+${Math.round(effect.speedBoost * 100)}% speed${effect.durationSec ? ` for ${effect.durationSec}s` : ''}`);
  if (effect.arrows) parts.push(`${effect.arrows} arrows`);
  return parts.join(', ');
}

function buildItemTooltip(item, quantity) {
  const lines = [];
  lines.push(item.name || item.itemId);
  lines.push(`Quantity: ${quantity}`);
  const typeLabel = item.type || item.itemType;
  if (typeLabel) lines.push(`Type: ${typeLabel}`);
  if (item.tier != null) lines.push(`Tier: ${item.tier}`);
  const effectText = formatItemEffect(item.effect);
  if (effectText) lines.push(effectText);
  return lines.join('\n');
}

function buildAuthHeaders() {
  const token = localStorage.getItem('jwt_token');
  const headers = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const apiKey = process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key';
  if (apiKey) {
    headers['X-API-Key'] = apiKey;
  }
  return headers;
}

function useInventory(heroId, isOwner) {
  const [heroInventory, setHeroInventory] = useState([]);
  const [userInventory, setUserInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchInventory = async () => {
    if (!heroId || !isOwner) return;
    setLoading(true);
    setError(null);
    try {
      const headers = buildAuthHeaders();
      const [heroRes, userRes] = await Promise.all([
        fetch(`/api/heroes/${heroId}/inventory`, { headers }),
        fetch(`/api/inventory/user`, { headers }),
      ]);

      if (!heroRes.ok) {
        throw new Error(`Failed to load hero inventory (${heroRes.status})`);
      }
      if (!userRes.ok) {
        throw new Error(`Failed to load user inventory (${userRes.status})`);
      }

      const heroData = await heroRes.json().catch(() => ({}));
      const userData = await userRes.json().catch(() => ({}));

      setHeroInventory(Array.isArray(heroData?.inventory) ? heroData.inventory : []);
      setUserInventory(Array.isArray(userData?.inventory) ? userData.inventory : []);
    } catch (err) {
      console.error('[LootTab] Error fetching inventory', err);
      setError(err instanceof Error ? err.message : 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (heroId && isOwner) {
      fetchInventory();
    }
  }, [heroId, isOwner]);

  return {
    heroInventory,
    userInventory,
    loading,
    error,
    refresh: fetchInventory,
    setHeroInventory,
    setUserInventory,
  };
}

export function LootTab({ heroId, isOwner, themeId }) {
  const {
    heroInventory,
    userInventory,
    loading,
    error,
    refresh,
    setHeroInventory,
    setUserInventory,
  } = useInventory(heroId, isOwner);
  const [transferError, setTransferError] = useState(null);
  const [transferring, setTransferring] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedDirection, setSelectedDirection] = useState(null);
  const [selectedQuantity, setSelectedQuantity] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [dragContext, setDragContext] = useState(null);

  const groupedHeroInventoryByContainer = useMemo(() => {
    const groups = new Map();
    heroInventory.forEach((item) => {
      const rawSlot = item.equipmentSlot ?? item.equipment_slot ?? null;
      const key = rawSlot && CONTAINER_LABELS[rawSlot] ? rawSlot : 'general';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(item);
    });
    groups.forEach((items) => {
      items.sort((a, b) => (a.slotNumber ?? a.slot_number ?? 0) - (b.slotNumber ?? b.slot_number ?? 0));
    });
    return groups;
  }, [heroInventory]);

  const userItems = useMemo(() => {
    return [...userInventory].sort((a, b) => {
      const slotA = a.slotNumber ?? a.slot_number ?? 0;
      const slotB = b.slotNumber ?? b.slot_number ?? 0;
      return slotA - slotB;
    });
  }, [userInventory]);

  const persistHeroInventory = async ({ updatedInventory, reorderPayload }) => {
    if (!reorderPayload || reorderPayload.inventoryId == null) {
      console.warn('[LootTab] Missing reorder payload; refreshing inventory');
      await refresh();
      return;
    }

    let lastErrorMessage = null;

    if (socketService.isSocketConnected()) {
      try {
        const response = await socketService.reorderHeroInventory(reorderPayload);
        if (Array.isArray(response?.inventory)) {
          setHeroInventory(response.inventory);
        }
        setTransferError(null);
        return;
      } catch (error) {
        lastErrorMessage =
          error instanceof Error ? error.message : 'Socket reorder failed';
        console.error('[LootTab] Socket reorder failed, falling back to API', error);
      }
    }

    try {
      const headers = buildAuthHeaders();
      const response = await fetch(`/api/inventory/hero/${heroId}/reorder`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          inventoryId: reorderPayload.inventoryId,
          targetSlot: reorderPayload.targetSlot,
        }),
      });

      if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(text || `Failed to reorder inventory (${response.status})`);
      }

      const data = await response.json().catch(() => ({}));
      if (Array.isArray(data?.inventory)) {
        setHeroInventory(data.inventory);
      }
      setTransferError(null);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : lastErrorMessage || 'Failed to save new container order.';
      console.error('[LootTab] Failed to persist hero inventory order', error);
      setTransferError(message);
      await refresh();
    }
  };

  const handleDragStart = (event, item, containerKey, hasFixedSlots, direction) => {
    if (!hasFixedSlots || direction !== 'hero_to_user') return;
    const itemId = item.id;
    if (itemId == null) return;
    const slotNumber = Number(item.slotNumber ?? item.slot_number ?? -1);
    setDragContext({
      itemId,
      containerKey,
      slotNumber,
    });
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(itemId));
  };

  const handleDragEnd = () => {
    setDragContext(null);
  };

  const handleDragOver = (event, hasFixedSlots, direction) => {
    if (!dragContext || !hasFixedSlots || direction !== 'hero_to_user') return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const handleDropOnSlot = async (event, targetSlot, containerKey, hasFixedSlots, direction) => {
    if (!dragContext || !hasFixedSlots || direction !== 'hero_to_user') return;
    event.preventDefault();

    const { itemId, containerKey: sourceContainer, slotNumber: sourceSlot } = dragContext;
    if (sourceContainer !== containerKey) {
      setDragContext(null);
      return;
    }

    const normalizedTargetSlot = Number(targetSlot);
    if (!Number.isFinite(normalizedTargetSlot) || normalizedTargetSlot < 0) {
      setDragContext(null);
      return;
    }

    const currentInventory = [...heroInventory];
    const draggedItemIndex = currentInventory.findIndex((entry) => entry.id === itemId);
    if (draggedItemIndex === -1) {
      setDragContext(null);
      return;
    }

    const draggedItem = currentInventory[draggedItemIndex];
    const currentSlot = Number(draggedItem.slotNumber ?? draggedItem.slot_number ?? -1);

    if (currentSlot === normalizedTargetSlot) {
      setDragContext(null);
      return;
    }

    const targetIndex = currentInventory.findIndex(
      (entry) =>
        entry.id !== itemId &&
        (entry.equipmentSlot ?? entry.equipment_slot ?? null) === containerKey &&
        Number(entry.slotNumber ?? entry.slot_number ?? -1) === normalizedTargetSlot
    );

    const updatedInventory = currentInventory.map((entry, idx) => {
      if (idx === draggedItemIndex) {
        return {
          ...entry,
          slotNumber: normalizedTargetSlot,
          slot_number: normalizedTargetSlot,
        };
      }

      if (targetIndex !== -1 && idx === targetIndex) {
        const fallbackSlot = currentSlot >= 0 ? currentSlot : normalizedTargetSlot;
        return {
          ...entry,
          slotNumber: fallbackSlot,
          slot_number: fallbackSlot,
        };
      }

      return entry;
    });

    setHeroInventory(updatedInventory);
    setDragContext(null);

    const reorderPayload = {
      heroId,
      inventoryId: Number(itemId),
      targetSlot: normalizedTargetSlot,
      sourceSlot,
      containerKey,
    };

    setTransferError(null);
    await persistHeroInventory({ updatedInventory, reorderPayload });
  };

  const handleTransfer = async () => {
    if (!selectedItem || !selectedDirection || !heroId) return;
    const availableQty = Number(selectedItem.quantity ?? selectedItem.qty ?? 0);
    const parsedQty = Number(selectedQuantity);
    const qty = Number.isFinite(parsedQty) && parsedQty > 0 ? Math.min(parsedQty, availableQty) : availableQty;
    if (qty <= 0) return;

    setTransferring(true);
    setTransferError(null);
    try {
      const headers = buildAuthHeaders();
      const response = await fetch('/api/inventory/transfer', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          heroId,
          itemId: selectedItem.itemId || selectedItem.item_id,
          quantity: qty,
          direction: selectedDirection,
        }),
      });
      if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(text || `Transfer failed (${response.status})`);
      }

      const data = await response.json().catch(() => ({}));
      if (data?.heroInventory) {
        setHeroInventory(data.heroInventory);
      }
      if (data?.userInventory) {
        setUserInventory(data.userInventory);
      }
      setSelectedItem(null);
      setSelectedDirection(null);
      setSelectedQuantity('');
      setShowModal(false);
    } catch (err) {
      console.error('[LootTab] Transfer error', err);
      setTransferError(err instanceof Error ? err.message : 'Transfer failed');
    } finally {
      setTransferring(false);
    }
  };

  const renderContainerSection = (title, items, direction, containerKey = 'general') => {
    const capacity = CONTAINER_CAPACITY[containerKey] ?? Infinity;
    const itemCount = items.length;
    const hasFixedSlots = Number.isFinite(capacity);
    const capacityLabel = hasFixedSlots ? `${itemCount}/${capacity}` : `${itemCount}/∞`;

    const displayEntries = (() => {
      if (!hasFixedSlots) {
        return { entries: items.map((item) => ({ type: 'item', item })), firstSlot: null };
      }

      const slotMap = new Map();
      const overflow = [];
      items.forEach((item) => {
        const slotNumber = Number(item.slotNumber ?? item.slot_number);
        if (Number.isFinite(slotNumber)) {
          slotMap.set(slotNumber, item);
        } else {
          overflow.push(item);
        }
      });

      const entries = [];
      let firstSlot = null;
      const emptyIndices = [];
      for (let i = 0; i < Number(capacity); i += 1) {
        if (slotMap.has(i)) {
          const slotEntry = { type: 'item', item: slotMap.get(i), slotNumber: i };
          if (firstSlot == null) firstSlot = i;
          entries.push(slotEntry);
        } else {
          emptyIndices.push(i);
        }
      }

      overflow.forEach((item) => {
        entries.push({ type: 'item', item, slotNumber: item.slotNumber ?? item.slot_number ?? null });
      });

      emptyIndices.forEach((slotNumber) => {
        entries.push({ type: 'empty', slotNumber });
      });

      return { entries, firstSlot };
    })();

    const entriesArray = displayEntries.entries;
    const firstSlotIndex = hasFixedSlots ? displayEntries.firstSlot : null;

    return (
      <div className="bg-gray-800 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-white">{title}</h3>
          <span className="text-sm text-gray-400">{capacityLabel}</span>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-9 xl:grid-cols-9 gap-1">
          {entriesArray.map((entry, idx) => {
            if (entry.type === 'empty') {
              const placeholderKey = `${direction}:${containerKey}:empty:${entry.slotNumber ?? idx}`;
              return (
                <div
                  key={placeholderKey}
                  className="relative bg-gray-900/30 border border-dashed border-gray-700/50 rounded-md p-1 flex flex-col items-center justify-center text-[9px] text-gray-500 uppercase tracking-wide transition shadow-sm"
                  onDragEnter={(event) => handleDragOver(event, hasFixedSlots, direction)}
                  onDragOver={(event) => handleDragOver(event, hasFixedSlots, direction)}
                  onDrop={(event) => handleDropOnSlot(event, entry.slotNumber ?? idx, containerKey, hasFixedSlots, direction)}
                >
                  <div className="relative w-full pb-[55%] mb-0.5">
                    <div className="absolute inset-0 flex items-center justify-center rounded-sm border border-dashed border-gray-700/60 text-[9px] text-gray-500 tracking-[0.2em] uppercase">
                      Empty
                    </div>
                  </div>
                  <div className="text-[8px] text-gray-600 uppercase tracking-wide">
                    Slot {Number(entry.slotNumber ?? idx) + 1}
                  </div>
                </div>
              );
            }

            const item = entry.item;
            const quantity = Number(item.quantity ?? item.qty ?? 0) || 0;
            const iconKey = item.iconKey ?? item.icon_key ?? null;
            const iconUrl = formatItemIcon(item.themeId || item.theme_id || themeId, iconKey);
            const emoji = TYPE_EMOJI[item.type || item.itemType] || '📦';
            const tooltip = buildItemTooltip(item, quantity);
            const inventoryPrimaryKey = item.id ?? item.inventoryId ?? `${item.itemId}-${idx}`;
            const itemKey = `${direction}:${containerKey}:${inventoryPrimaryKey}`;
            const canDrag = hasFixedSlots && direction === 'hero_to_user';
            const targetSlot = entry.slotNumber ?? idx;
            const isNext =
              hasFixedSlots &&
              direction === 'hero_to_user' &&
              firstSlotIndex != null &&
              entry.slotNumber === firstSlotIndex;

            return (
              <button
                key={itemKey}
                type="button"
                title={tooltip}
                draggable={canDrag}
                onDragStart={(event) => handleDragStart(event, item, containerKey, hasFixedSlots, direction)}
                onDragEnd={handleDragEnd}
                onDragOver={(event) => handleDragOver(event, hasFixedSlots, direction)}
                onDrop={(event) => handleDropOnSlot(event, targetSlot, containerKey, hasFixedSlots, direction)}
                onClick={() => {
                  setSelectedItem(item);
                  setSelectedDirection(direction);
                  setSelectedQuantity('');
                  setShowModal(true);
                }}
                className={`group relative bg-gray-900/60 ${
                  isNext
                    ? 'border border-green-500/80 shadow-[0_0_10px_rgba(34,197,94,0.45)]'
                    : 'border border-gray-700/60'
                } hover:border-blue-500/70 hover:bg-gray-800/80 rounded-md p-1 flex flex-col items-center transition shadow-sm`}
              >
                {isNext && (
                  <div className="absolute -top-2 inset-x-0 flex justify-center pointer-events-none">
                    <span className="bg-green-500 text-green-950 text-[8px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shadow-sm">
                      Next
                    </span>
                  </div>
                )}
                <div className="relative w-full pb-[55%] mb-0.5">
                  {iconUrl ? (
                    <>
                      <img
                        src={iconUrl}
                        alt={item.name || item.itemId}
                        className="absolute inset-0 w-full h-full object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const fallback = e.currentTarget.parentElement?.querySelector('.fallback-emoji');
                          if (fallback) fallback.classList.remove('hidden');
                        }}
                      />
                      <div className="fallback-emoji hidden absolute inset-0 flex items-center justify-center text-base">
                        {emoji}
                      </div>
                    </>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-base">
                      {emoji}
                    </div>
                  )}
                  <span className="absolute bottom-0.5 right-0.5 bg-black/70 text-[12px] px-1 py-0.5 rounded-full text-white">
                    ×{quantity}
                  </span>
                </div>
                <div className="text-[9px] text-gray-200 text-center truncate w-full">
                  {item.name || item.itemId}
                </div>
                {item.type && (
                  <div className="text-[7px] text-gray-500 mt-0.5 uppercase tracking-wide">
                    {item.type}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  if (!isOwner) {
    return (
      <div className="bg-gray-800 rounded-lg p-6 text-gray-300">
        <h3 className="text-lg font-semibold text-white mb-2">Hero Inventory</h3>
        <p>This hero belongs to another player. Inventory details are hidden for privacy.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-lg p-6 text-gray-300">
        <p>Loading inventory...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-800 rounded-lg p-6 text-red-300">
        <p>Error loading inventory: {error}</p>
        <button
          type="button"
          className="mt-3 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded text-white text-sm font-semibold"
          onClick={refresh}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {transferError && (
        <div className="bg-red-900/40 border border-red-600 text-red-200 px-4 py-3 rounded">
          <div className="flex items-center justify-between">
            <span>{transferError}</span>
            <button
              type="button"
              onClick={() => setTransferError(null)}
              className="text-red-200 hover:text-red-100 text-sm"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">Hero &amp; Vault Inventory</h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={refresh}
            disabled={transferring}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-sm font-semibold text-gray-100 rounded"
          >
            Refresh
          </button>
        </div>
      </div>
      <p className="text-xs text-gray-400">
        Arrange your items the way you want them used in battle. The AI will use them in order.
      </p>

      {['battle_container', 'healing_container', 'magic_container', 'arrow_container', 'poison_container'].map((key) => (
        <React.Fragment key={key}>
          {renderContainerSection(
            formatContainerLabel(key),
            groupedHeroInventoryByContainer.get(key) ?? [],
            'hero_to_user',
            key
          )}
        </React.Fragment>
      ))}
      {Array.from(groupedHeroInventoryByContainer.entries())
        .filter(([key]) => !['battle_container', 'healing_container', 'magic_container', 'arrow_container', 'poison_container'].includes(key))
        .map(([key, items]) => (
          <React.Fragment key={key}>
            {renderContainerSection(
              formatContainerLabel(key === 'general' ? null : key),
              items,
              'hero_to_user',
              key
            )}
          </React.Fragment>
        ))}

      {renderContainerSection('Vault Inventory', userItems, 'user_to_hero', 'general')}

      {showModal && selectedItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-gray-900 border border-gray-700 rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white">Transfer Item</h3>
                <p className="text-sm text-gray-400">
                  {selectedItem.name || selectedItem.itemId}
                  <span className="ml-2 text-gray-500">
                    {selectedDirection === 'hero_to_user' ? 'Hero → Vault' : 'Vault → Hero'}
                  </span>
                </p>
              </div>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-200"
                onClick={() => {
                  setShowModal(false);
                  setSelectedItem(null);
                  setSelectedDirection(null);
                  setSelectedQuantity('');
                }}
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Quantity</label>
                <input
                  type="number"
                  min="1"
                  max={Number(selectedItem.quantity ?? selectedItem.qty ?? 0) || undefined}
                  value={selectedQuantity}
                  onChange={(event) => setSelectedQuantity(event.target.value)}
                  placeholder={`Max ${Number(selectedItem.quantity ?? selectedItem.qty ?? 0) || 1}`}
                  className="w-full bg-gray-800 border border-gray-700 text-gray-100 rounded px-3 py-2 text-sm"
                  disabled={transferring}
                />
              </div>

              <button
                type="button"
                disabled={transferring}
                onClick={handleTransfer}
                className={`w-full px-4 py-2 rounded text-sm font-semibold transition ${
                  selectedDirection === 'hero_to_user'
                    ? 'bg-indigo-600 hover:bg-indigo-500 text-white'
                    : 'bg-green-600 hover:bg-green-500 text-white'
                } ${transferring ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {selectedDirection === 'hero_to_user' ? 'Send to Vault' : 'Equip Hero'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LootTab;

